import { useMemo } from "react";
import {
  createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel
} from "@tanstack/react-table";
import { type BusinessSubscription } from "lens-platform-sdk";

import { dateToString } from "src/utils/date";
import { getPlanName } from "src/services/plans";
import { type CanceledSeat, isCanceledSeat } from "src/services/getSubscriptionSeatState";
import { trimSubscriptionId } from "src/services/trimSubscriptionId";

import { UsernameTableCell } from "src/components/Business/UsernameTableCell";
import Table from "src/components/Table/Table";

import styles from "src/components/Business/CanceledSeatTable.module.css";
import { useTrackedTableSort } from "./useTrackedTableSort";
import { useLensPlatformClient } from "../../hooks/useLensPlatformClient";

const canceledTableColumnHelper = createColumnHelper<CanceledSeat>();

const canceledColumns = [
  canceledTableColumnHelper.accessor("user", {
    header: () => <span>Lens ID</span>,
    cell: ({ getValue }) => {
      const user = getValue();

      return (
        <UsernameTableCell username={user?.username} fullname={user?.fullname} />
      );
    },
    meta: { primary: true }
  }),
  canceledTableColumnHelper.accessor("subscription", {
    header: () => <span>Subscription</span>,
    cell: ({ getValue }) => {
      const subscription = getValue();

      if (!subscription) {
        return null;
      }
      const { planCode, id } = subscription;

      return (
        <div className={styles.subscription}>
          <div className={styles.subscriptionPlanName}>{getPlanName(planCode)}</div>
          <div className={styles.subscriptionId} title={id}>{trimSubscriptionId(id)}</div>
        </div>
      );
    }
  }),
  canceledTableColumnHelper.accessor("subscription.currentPeriodEndsAt", {
    header: () => <span>Subscription Valid Until</span>,
    cell: ({ getValue }) => {
      const currentPeriodEndsAt = getValue();

      if (currentPeriodEndsAt === null) {
        return null;
      }
      const validUntil = dateToString(new Date(currentPeriodEndsAt));

      return (
        <div className={styles.validUntil}>
          <div>{validUntil}</div>
        </div>
      );
    }
  }),
  canceledTableColumnHelper.accessor("deactivatedAt", {
    header: () => <span>Deactivation Date</span>,
    cell: ({ getValue }) => {
      const deactivatedAt = dateToString(new Date(getValue()));

      return (
        <div className={styles.deactivatedAt}>
          <div>{deactivatedAt}</div>
        </div>
      );
    }
  }),
  canceledTableColumnHelper.accessor("offline", {
    header: () => <span>Offline</span>,
    cell: ({ getValue }) => (
      <div className={styles.offlineContainer}>
        {getValue() && <span title="Seat used offline" className={styles.offline} />}
      </div>
    )
  })
];

const caption = "Canceled seats";

interface Props {
  businessSubscriptions: BusinessSubscription[];
}

const CanceledSeatTable = ({ businessSubscriptions }: Props) => {
  const platformClient = useLensPlatformClient();
  const canceledTableData = useMemo<CanceledSeat[]>(() => {
    const useSeats = businessSubscriptions
      .map(({ usedSeats }) => usedSeats)
      .flat()
      .filter(isCanceledSeat);

    return useSeats.map((usedSeat) => ({
      ...usedSeat,
      subscription: businessSubscriptions.find(({ id }) => id === usedSeat.subscriptionId),
      user: usedSeat.user ? {
        ...usedSeat.user,
        fullname: usedSeat.user ? platformClient.user.getUserFullName(usedSeat.user) : ""
      } : null
    }));
  }, [businessSubscriptions, platformClient.user]);
  const { sorting, onSortingChange } = useTrackedTableSort({ table: caption });

  const canceledTable = useReactTable({
    data: canceledTableData,
    columns: canceledColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting
    },
    onSortingChange
  });
  const canceledTableRowModel = canceledTable.getRowModel();
  const canceledTableRows = useMemo(() => canceledTableRowModel.rows, [canceledTableRowModel]);

  return (
    <Table<CanceledSeat>
      caption={caption}
      className={styles.table}
      rows={canceledTableRows}
      columns={canceledTable.getAllColumns()}
      headerGroups={canceledTable.getHeaderGroups()}
      headerGroupProps={{
        cellClassName: styles.tableHeader
      }}
      rowProps={{
        className: styles.tableRow,
        cellClassName: styles.tableCell
      }}
      noDataText="No canceled seats on your Lens Business ID!"
    />
  );
};

export default CanceledSeatTable;
